import { Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PlateComponent,
  JjamesButtonComponent,
  RegionSeparator,
  findPlateCountry,
  normalizePlate,
  sanitizePlateInput
} from 'shared-lib';

import {
  ParkerReceiptDto,
  PublicPaymentService,
  SettlementQuoteSource,
  StayPaymentStatus,
  PublicStaySettlementQuoteDto
} from '../../../api/gen';
import { ParkerStateService } from '../../services/parker-state.service';

interface PlatePresentation {
  fullNumber: string;
  euBand: string;
  countryCode: string;
  region: string;
  regionSeparator: RegionSeparator;
}

/**
 * Guest settlement — pay for a stay with nothing but the number plate.
 *
 * This is the only route to paying for someone with no account and no QR code: the driver who left a
 * free-flow site unpaid, got a bill, and is now looking for somewhere to settle it. The column has a
 * plate keypad and a registered parker has their stays on the activity page; this closes the third
 * case.
 *
 * <b>Why the quote and not the old plate lookup.</b> This panel used to call
 * `parkingProcessPublicByLicensePlate`, which returns only a stay that is still <em>running</em>. On
 * a free-flow site the exit read ends the stay (KAN-458 D-21), so by the time anybody owes money that
 * lookup answers "nothing found" — precisely for the stay being paid for. `quote/by-plate` reaches
 * ended stays too, and prefers a frozen obligation over a re-price, so the figure here is the one on
 * the invoice rather than a second opinion.
 *
 * Every amount is the server's. Nothing on this screen is derived, rounded, or summed client-side;
 * the pay button hands the parking-process id to the payment app, which quotes again before charging.
 */
@Component({
  selector: 'app-license-plate',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CurrencyPipe,
    DatePipe,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    PlateComponent,
    JjamesButtonComponent,
    TranslatePipe
  ],
  templateUrl: './license-plate.component.html',
  styleUrl: './license-plate.component.scss'
})
export class LicensePlateComponent {
  private readonly paymentService = inject(PublicPaymentService);
  private readonly parkerState = inject(ParkerStateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  /**
   * Restricts the lookup to one site — set by the site page (`/site/:parkingSpaceId`, the QR on a
   * site's printed signs). Null on the home page, which searches every site.
   */
  readonly parkingSpaceId = input<string | null>(null);

  licensePlate = '';

  readonly isLoading = signal(false);
  readonly hasSearched = signal(false);

  /** The server's quote for the most recent stay on this plate, or null when it found none. */
  readonly quote = signal<PublicStaySettlementQuoteDto | null>(null);

  /** The plate that produced the quote on screen — not the box, which the driver may still be editing. */
  readonly searchedPlate = signal('');

  readonly amountDue = computed(() => this.quote()?.amountDue ?? 0);
  readonly isPayable = computed(() => this.amountDue() > 0);

  /** A stay with no exit is still running: the driver can pay now and drive out within the grace window. */
  readonly isActive = computed(() => !!this.quote() && !this.quote()!.endedAt);

  /**
   * True when this amount is a frozen bill rather than a running total.
   *
   * Worth saying out loud on the screen: a driver who left hours ago expects the price to have kept
   * climbing, and telling them it has stopped is the difference between paying now and putting it off.
   */
  readonly isFrozen = computed(() => !!this.quote()?.obligationId);

  /**
   * Why nothing is owed, when nothing is owed. The distinctions matter to the person reading:
   * "you already paid", "your pass covers this" and "you have ten minutes to drive out" are three
   * different pieces of news, and only one of them means they can stop thinking about it.
   */
  readonly nothingDueKey = computed(() => {
    switch (this.quote()?.source) {
      // Settled with no money behind it (a zero-priced stay) is free, not paid.
      case SettlementQuoteSource.AlreadySettled:
        return this.quote()?.paymentStatus === StayPaymentStatus.Paid
          ? 'GUEST_PAY.DONE_ALREADY_PAID' : 'GUEST_PAY.DONE_FREE';
      case SettlementQuoteSource.CoveredZero: return 'GUEST_PAY.DONE_COVERED';
      case SettlementQuoteSource.NotPriceable: return 'GUEST_PAY.DONE_NOT_PRICEABLE';
      // Paid, and inside the window to drive out. Deliberately NOT "free of charge": they paid, and
      // the useful fact is the deadline, which `graceUntil` carries.
      case SettlementQuoteSource.PaidWithinGrace: return 'GUEST_PAY.DONE_GRACE';
      default: return 'GUEST_PAY.DONE_FREE';
    }
  });

  /** When the drive-out window closes, for the grace message. Null when the server sent none. */
  readonly graceUntil = computed(() => this.quote()?.graceUntil ?? null);

  /** Money was taken for this stay, so there is a receipt to offer. */
  readonly isPaid = computed(() => this.quote()?.paymentStatus === StayPaymentStatus.Paid);

  readonly receiptsLoading = signal(false);
  /** Null until asked; an empty list is "no receipt for this stay yet". */
  readonly receipts = signal<ParkerReceiptDto[] | null>(null);

  /**
   * Rejects everything but A–Z, Ä/Ö/Ü and digits as it is typed or pasted, restoring the caret by
   * hand so a stray character typed mid-plate does not throw the user to the end of the field.
   * Mirrors RegisterLicensePlateComponent.
   */
  protected onPlateFieldInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    const cleaned = sanitizePlateInput(raw);
    if (cleaned === raw) {
      return;
    }

    const caret = input.selectionStart ?? raw.length;
    const rejectedBeforeCaret = caret - sanitizePlateInput(raw.slice(0, caret)).length;
    this.licensePlate = cleaned;
    input.value = cleaned;
    const restored = Math.max(0, caret - rejectedBeforeCaret);
    input.setSelectionRange(restored, restored);
  }

  search(): void {
    // Normalised here because this is where the driver's typing enters the system: plates are stored
    // without separators, so "L-1234 PP" has to become "L1234PP" or it matches nothing.
    const plate = normalizePlate(this.licensePlate);
    if (!plate) {
      this.snackBar.open(this.translate.instant('GUEST_PAY.ENTER_PLATE'), 'OK', { duration: 3000 });
      return;
    }

    this.isLoading.set(true);
    this.hasSearched.set(false);
    this.quote.set(null);
    this.receipts.set(null);

    // POST with the plate in the body: a plate in the URL lands in request logs and browser history.
    const siteId = this.parkingSpaceId();
    const quote$ = siteId
      ? this.paymentService.publicPaymentSiteParkingSpaceIdQuoteByPlatePost(siteId, { licensePlate: plate })
      : this.paymentService.publicPaymentQuoteByPlatePost({ licensePlate: plate });

    quote$.subscribe({
      next: (result) => {
        this.quote.set(result);
        this.searchedPlate.set(plate);
        this.hasSearched.set(true);
        this.isLoading.set(false);
      },
      error: (err) => {
        // 404 is the ordinary answer for a plate that has never parked here, and it is shown as
        // "nothing found" rather than as a failure. Anything else is a real fault and says so —
        // telling a driver they owe nothing because a lookup broke sends them away with a debt.
        this.searchedPlate.set(plate);
        this.hasSearched.set(true);
        this.isLoading.set(false);

        if (err?.status !== 404) {
          console.error('Failed to quote a stay by plate', err);
          this.snackBar.open(this.translate.instant('GUEST_PAY.ERROR'), 'OK', { duration: 5000 });
        }
      }
    });
  }

  clear(): void {
    this.licensePlate = '';
    this.quote.set(null);
    this.searchedPlate.set('');
    this.hasSearched.set(false);
    this.receipts.set(null);
  }

  /**
   * Opening a receipt is downloading it: the server stops offering it to the plate lookup, and it drops
   * off this list. The link itself opens regardless — marking is bookkeeping, not a gate on the parker
   * getting their copy. One request per tap.
   */
  markDownloaded(receipt: ParkerReceiptDto): void {
    const parkingProcessId = this.quote()?.parkingProcessId;
    if (!parkingProcessId) return;

    this.paymentService.publicPaymentReceiptsByPlateLicensePlateDownloadedPost(
      this.searchedPlate(),
      { parkingProcessId, receiptId: receipt.receiptId }
    ).subscribe({
      next: () => this.receipts.update(list => list?.filter(r => r.receiptId !== receipt.receiptId) ?? null),
      // Left on the list: the parker already has the receipt open, and the next lookup offers it again.
      error: (err) => console.error('Failed to mark receipt downloaded', err)
    });
  }

  /**
   * The receipts of the stay on screen — one request, on click only. Named by its id, so the server
   * hands over this stay's receipt only when the stay is this plate's, and only until it is downloaded.
   */
  loadReceipts(): void {
    const parkingProcessId = this.quote()?.parkingProcessId;
    if (!parkingProcessId || this.receiptsLoading()) return;

    this.receiptsLoading.set(true);

    this.paymentService.publicPaymentReceiptsByPlateLicensePlatePost(
      this.searchedPlate(),
      { parkingProcessId }
    ).subscribe({
      next: (result) => {
        this.receipts.set(result ?? []);
        this.receiptsLoading.set(false);
      },
      error: (err) => {
        this.receiptsLoading.set(false);
        // 429 is the per-plate limit, not a fault — say so, or the parker keeps retrying into it.
        const key = err?.status === 429 ? 'GUEST_PAY.TOO_MANY_ATTEMPTS' : 'GUEST_PAY.ERROR';
        if (err?.status !== 429) console.error('Failed to load receipts by plate', err);
        this.snackBar.open(this.translate.instant(key), 'OK', { duration: 5000 });
      }
    });
  }

  /**
   * Hands off to the payment app, which quotes again and charges. Nothing is charged from here.
   *
   * The return target is passed explicitly and must stay that way. `payStay` defaults to `/activity`,
   * which is right for a signed-in parker paying from their own list and wrong for everyone who
   * reaches this panel: that route is behind `canActivateAuthRole` with `Role.RegisteredParker`, so a
   * guest coming back from Stripe is bounced to `/forbidden` — a route this app does not define, and
   * there is no wildcard to catch it. They would pay and land on a blank screen.
   */
  pay(): void {
    // A site page sends the parker back to itself, so "back" on the payment page is a new search here.
    const siteId = this.parkingSpaceId();
    const returnTo = siteId ? `${window.location.origin}/site/${siteId}` : `${window.location.origin}/`;
    this.parkerState.payStay(this.quote()?.parkingProcessId, returnTo);
  }

  getPlatePresentation(plateNumber?: string | null): PlatePresentation {
    const norm = normalizePlate(plateNumber);
    const country = 'AT';
    return {
      fullNumber: norm,
      countryCode: country,
      euBand: findPlateCountry(country)?.euBand ?? country,
      region: '',
      regionSeparator: findPlateCountry(country)?.regionSeparator ?? 'none'
    };
  }

  getDuration(startedAt?: string | null, endedAt?: string | null): string {
    if (!startedAt) return '—';
    const start = new Date(startedAt).getTime();
    const end = endedAt ? new Date(endedAt).getTime() : Date.now();
    const diffMs = Math.max(0, end - start);

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }
}
